import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import QuestionRenderer from "./components/QuestionRenderer";
import ProgressBar from "./components/ProgressBar";
import "./dashboardStyle.css";
import { useDispatch, useSelector } from "react-redux";
import { saveSurvey, surveyDetail } from "../redux/slice/feedbackSlice";

const SurveyDetail = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const surveys = useSelector((state) => state.feedbackSurvey);
  const apiSurveyData = surveys?.detailSurvey?.data;
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    dispatch(surveyDetail(surveyId));
  }, [dispatch, surveyId]);

  // Transform API data to match component structure
  const transformApiDataToSurvey = (apiData) => {
    if (!apiData) return null;

    return {
      id: apiData._id,
      title: apiData.title,
      description: apiData.description,
      reward: apiData.reward?.amount || 0,
      currency: apiData.reward?.currency === 'INR' ? '₹' : apiData.reward?.currency,
      questions: apiData.questions.map((q) => ({
        id: q._id,
        type: q.questionType === 'image_upload' ? 'image' : q.questionType === 'text' ? (q.required ? 'text' : 'text-long') : q.questionType,
        question: q.questionText,
        options: q.options?.map(opt => opt.optionText) || [],
        placeholder: q.questionType === 'text' ? 'Enter your answer...' : undefined,
        accept: q.questionType === 'image_upload' ? 'image/*' : undefined,
        required: q.required
      }))
    };
  };

  const currentSurvey = transformApiDataToSurvey(apiSurveyData);

  // Use API data if available
  const survey = currentSurvey;

  // Redirect if survey not found and not loading
  useEffect(() => {
    if (!survey && !surveys?.loading && apiSurveyData !== undefined) {
      alert("Survey not found!");
      navigate("/feedback");
    }
  }, [survey, surveys?.loading, apiSurveyData, navigate]);

  // Show loading state
  if (surveys?.loading) {
    return (
      <div className="survey-detail-container">
        <div className="container mt-4 min-h text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading survey...</p>
        </div>
      </div>
    );
  }

  if (!survey) return null;

  const currentQuestion = survey.questions[currentQuestionIndex];
  const totalQuestions = survey.questions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  const handleAnswer = (answer) => {
    // Store the answer
    setAnswers({
      ...answers,
      [currentQuestion.id]: answer,
    });
  };

  const handleNext = () => {
    if (!isLastQuestion) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const isCurrentAnswerValid = () => {
    const answer = answers[currentQuestion.id];
    if (currentQuestion.required === false) {
      return true;
    }
    if (answer === null || answer === undefined || answer === "") {
      return false;
    }
    if (currentQuestion.type === "multiple" && Array.isArray(answer)) {
      return answer.length > 0;
    }
    return true;
  };

  const handleSkip = () => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: "",
    });
    handleNext();
  };

  // Helper function to detect device type
  const getDeviceType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      return "tablet";
    }
    if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
      return "mobile";
    }
    return "desktop";
  };

  // Helper function to get user's IP address (placeholder)
  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Error fetching IP:', error);
      return 'unavailable';
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Calculate completion time in seconds
      const completionTime = Math.floor((Date.now() - startTime) / 1000);

      // Get user IP address
      const ipAddress = await getUserIP();

      // Transform answers object to responses array
      const responses = survey.questions.map((question) => ({
        questionId: question.id,
        answer: answers[question.id] || null,
      }));

      // Prepare final submission data according to required format
      const submissionData = {
        responses: responses,
        completionTime: completionTime,
        submittedAt: new Date().toISOString(),
        metadata: {
          ipAddress: ipAddress,
          userAgent: navigator.userAgent,
          device: getDeviceType(),
          source: "web",
          status: "COMPLETED"
        },
      };

      console.log("Submitting survey answers:", submissionData);

      // Submit survey via Redux
      await dispatch(saveSurvey({ surveyId: survey.id, responsedata: submissionData }));

   
      navigate("/feedback");
    } catch (error) {
      console.error("Error submitting survey:", error);
      alert("Failed to submit survey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  return (
    <div className="survey-detail-container">
      <div className="container mt-4 min-h">
        <ProgressBar
          current={currentQuestionIndex }
          total={totalQuestions}
        />

        <button className="btn btn-link" onClick={() => navigate("/feedback")}>
          ← Back to Surveys
        </button>

        <div className="question-container card p-4 mt-4">
          <QuestionRenderer
            question={currentQuestion}
            answer={answers[currentQuestion.id]}
            onAnswer={handleAnswer}
            questionNumber={currentQuestionIndex + 1}
          />

          <div className="navigation-buttons mt-4 d-flex justify-content-between">
            <button
              className="btn btn-secondary"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </button>

            {isLastQuestion ? (
              <button
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={currentQuestion.required !== false && !isCurrentAnswerValid() || isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            ) : (
              <div>
                 {currentQuestion.required === false && (
                  <button
                    className="btn btn-warning me-4"
                    onClick={handleSkip}
                  >
                    Skip
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={handleNext}
                  disabled={!isCurrentAnswerValid()}
                >
                  Next
                </button>
               
              </div>
            )}
          </div>
        </div>

      
      </div>
    </div>
  );
};

export default SurveyDetail;
